import { FC, useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Modal,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import LogoIcon from '../../assets/images/Logo.png';
import InformationIcon from '../../assets/icons/Info, Infornation.png';
import WorkTravelIcon from '../../assets/icons/Briefcase.png';
import FriendsIcon from '../../assets/icons/friends.png';
import FamilyIcon from '../../assets/icons/users.png';
import CouplesIcon from '../../assets/icons/users.png';
import SoloIcon from '../../assets/icons/building-city-town.png';
import LeftCloud from '../../assets/images/luggage 6 3.png';
import RightCloud from '../../assets/images/Frame 1597880801.png';
import Baggage from '../../assets/images/Frame 1597880802.png';
import Cactus from '../../assets/images/Frame 1597880800.png';
import HomeIcon from '../../assets/icons/Home.png';
import CardIcon from '../../assets/icons/card.png';
import CheckMarkIcon from '../../assets/icons/Checkmark, Done, Check.png';
import DiamondIcon from '../../assets/icons/diamond.png';
import ThunderIcon from '../../assets/icons/thunder-lightning-fast.png';
import BalanceIcon from '../../assets/icons/stash_balance-light.png';
import SofaIcon from '../../assets/icons/solar_sofa-linear.png';
import CheckedTableIcon from '../../assets/icons/checked_tebale_mark.png';
import OctaIcon from '../../assets/icons/Octa.png';
import RoboHandImg from '../../assets/images/robo-hand.png';

import { useGetLocationsQuery, useRegisterUserMutation } from '../../store/services/authApi';
import { useAppSelector } from '../../hooks/redux';
import { useIsMobile } from '../../hooks/useWindowWidth';

import CustomButton from '../../common/CustomButton';
import CustomSelect from '../../common/CustomSelect';
import CustomInput from '../../common/CustomInput';
import ToggleRadioButton from '../../common/ToggleRadioButton';
import RadioOption from '../../common/RadioOption';
import Plans from '../../components/Plans';
import { useGetActivityInterestQuery, useUpdateUserAdditionalInfoMutation } from '../../store/services/customizeOptionsApi';
import CustomAutoSelect from '../../common/CustomAutoSelect';
import { IPlan } from '../../components/Plans/Plans';

import styles from './PersonalizedForm.module.scss';

export type Values = {
  username?: string;
  gender?: string;
  agegroup?: string;
  country?: string;
  nearestCity?: string;
  nearestAirport?: string;
  unitsofMeasure?: string;
  travel?: string;
  collaborate?: string;
  plan?: number;
  budgetPreferences?: string;
  pace?: string;
  planningStyle?: string;
  travelPurpose?: string[];
  destinations?: string[];
  interests?: string[];
}

const PersonalizedForm: FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [activeStep, setActiveStep] = useState(0);
  const [countryInput, setCountryInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [airportInput, setAirportInput] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [airportSearch, setAirportSearch] = useState('');

  const [steps] = useState(['step1', 'step2', 'step3', 'step4', 'step5', 'step6', 'step7', 'step8', 'step9']);
  const [openModal, setOpenModal] = useState<{isOpen: boolean, content: IPlan[] | null}>({isOpen: false, content: null});
  const [acknowledge, setAcknowledge] = useState(false);
  const { data: countries } = useGetLocationsQuery({search: countrySearch, type: 'country'});
  const { data: cities } = useGetLocationsQuery({search: citySearch, type: 'city'});
  const { data: airports } = useGetLocationsQuery({search: airportSearch, type: 'airport'});
  const [registerUser] = useRegisterUserMutation();
  const { data: primaryTravelPurpose } = useGetActivityInterestQuery('primary_travel_purpose');
  const { data: typeOfDestination } = useGetActivityInterestQuery('type_of_destination');
  const { data: activityInterest } = useGetActivityInterestQuery('activity_interest');
  const { type: planType, plans, plan: planId } = useAppSelector((state) => state.plan);
  const [updateUserAdditionalInfo] = useUpdateUserAdditionalInfoMutation();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCountrySearch(countryInput);
    }, 500);

    return () => clearTimeout(timeout);
  }, [countryInput]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCitySearch(cityInput);
    }, 500);

    return () => clearTimeout(timeout);
  }, [cityInput]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAirportSearch(airportInput);
    }, 500);

    return () => clearTimeout(timeout);
  }, [airportInput]);

  const handleModalOpen = (content: IPlan[] | null = null) => {
    setOpenModal({ isOpen: true, content: content });
  };
  const handleModalClose = () => setOpenModal({ isOpen: false, content: null });

  const getAllAdvantages = () => {
    const map = new Map<string, Set<string>>();
    plans?.forEach((plan: IPlan) => {
      plan.advantages?.forEach(({ content, measure }) => {
        if (!map.has(content)) map.set(content, new Set());
        map.get(content)?.add(String(measure));
      });
    });
    return Array.from(map.keys());
  };

  const getMeasure = (plan: any, content: string) => {
    const adv = plan.advantages?.find((a: any) => a.content === content);
    return adv?.measure || '—';
  };

  const renderCellContent = (measure: string) => {
    if (measure === 'true') {
      return <img src={CheckedTableIcon} alt={measure} />
    }
    return measure;
  };

  const allAdvantages = getAllAdvantages();

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleSubmitNext = async (values: Values) => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);

    const savedData = JSON.parse(localStorage.getItem("createAccount") || "{}");
    setAcknowledge(false);
    try {
      const result = await registerUser({
        first_name: savedData.firstName,
        last_name: savedData.lastName,
        email: savedData.email,
        password: savedData.password,
        username: values.username || '',
        gender: values.gender || '',
        age_group: values.agegroup || '',
        country: values.country || '',
        chosen_plan: values.plan || 1,
      }).unwrap();

      console.log(result, 'result')

      if (result) {
        const url = new URL(result.plan_checkout_url);
        const transactionId = url.searchParams.get('_ptxn');
        window.open(`/checkout.html?&_ptxn=${transactionId}`, '_blank');
      }

      localStorage.setItem('userInfo', JSON.stringify({
        access: result.access,
        refresh: result.refresh,
      }));

      console.log('values', values)

    } catch (error) {
      console.error("Error registering user:", error);
    }
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleComplete = () => {};

  const initialValues = {
    username: '',
    gender: '',
    agegroup: '',
    country: '',
    nearestCity: '',
    nearestAirport: '',
    unitsofMeasure: '',
    travel: '',
    collaborate: '',
    plan: 1,
    budgetPreferences: '',
    pace: '',
    planningStyle: '',
    travelPurpose: [''],
    destinations: [''],
    interests: [''],
  };

  const handleSubmit = async (values: Values) => {

    try {
      await updateUserAdditionalInfo({
        nearest_city: values.nearestCity,
        nearest_airport: values.nearestAirport,
        unit_system: values.unitsofMeasure === 'Miles/°F' ? 'Metric' : 'Imperial',
        travel_preference: values.travel?.split(' ')[0],
        is_inviting_others: values.collaborate === 'yes',
        budget_preference: values.budgetPreferences,
        pace_preference: values.pace,
        primary_travel_purposes: (values.travelPurpose ?? [])[0]?.length ? values.travelPurpose : [],
        destination_types: (values.destinations ?? [])[0].length ? values.destinations : [],
        activity_interests: (values.interests ?? [])[0].length ? values.interests : [],
        trip_planning_style: values.planningStyle,
      });

      navigate('/');
    } catch (error) {
      console.error("Error passing additional user params:", error);
    }

    alert('should be connected BE to the additional questions')
  };

  const formikValidate = (values: Values) => {
    const errors: Values = {};
    if (!values.username) {
      errors.username = 'Username is required';
    }
    if (!values.gender && !values.username) {
      errors.gender = 'Gender is required';
    }
    if (!values.agegroup && !values.gender && !values.username) {
      errors.agegroup = 'Age group is required';
    }
    if (!values.country && !values.agegroup && !values.gender && !values.username) {
      errors.country = 'Country is required';
    }
    return errors;
  }

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;
  const isCompleted = activeStep === steps.length;

  return (
    <Container maxWidth="lg" sx={{ padding: '0 !important' }}>
      <Box className={styles.personalizedForm}>
        <Box className={styles.header}>
          <Box className={styles.logoContainer}>
            <Box className={styles.logo} onClick={() => navigate('/')}>
              <img className={styles.logoImg} src={LogoIcon} alt="logo" />
              <Typography variant='h8Bold'>TravGenius</Typography>
            </Box>
            {/* <CustomButton
              variant='contained'
              customClass={activeStep < 2 ? styles.hideUpgradeBtn : styles.upgradeBtn}
              onClick={() => handleModalOpen(null)}
            >
              Upgrade Plan
            </CustomButton> */}
          </Box>
          <Box className={styles.progressContainer}>
            <Box sx={{ height: '10px', backgroundColor: '#D9D9D9', width: '100%', borderRadius: '5px', margin: '10px 0px' }}>
              <Box
                sx={{
                  height: '10px',
                  background: 'linear-gradient(90deg, #51B888 0%, #17B2E8 100%)',
                  width: `${(activeStep + 1) * 100 / steps.length}%`,
                  borderRadius: '5px'
                }}
              />
            </Box>
            <Typography variant='h10' className={styles.steps}>
              {activeStep + 1} of {steps.length}
            </Typography>
          </Box>
        </Box>

        <Box className={styles.formWrapper}>
          <Box className={`${styles.imagesContainer}  ${activeStep != 0 && styles.hideImagesContainer}`}>
            <img src={LeftCloud} alt="Image 1"/>
            <img src={RightCloud} alt="Image 2"/>
            <img src={Baggage} alt="Image 3"/>
            <img src={Cactus} alt="Image 4"/>
          </Box>
          <Formik
            initialValues={initialValues}
            validate={formikValidate}
            onSubmit={handleSubmit}
          >
            {({ values, errors, handleChange, setFieldValue, isSubmitting }) => (
              <Form className={styles.stepsForm}>
                <Modal
                  open={openModal.isOpen}
                  onClose={handleModalClose}
                  aria-labelledby="plans"
                  aria-describedby="plans"
                >
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    maxWidth: 1100,
                    width: '100%',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: "20px",
                    p: 4,
                    overflowY: 'auto',
                    maxHeight: '90vh',
                  }}>
                    <Box sx={{ position: 'absolute', top: 20, right: 20, cursor: 'pointer' }}>
                      <CloseIcon onClick={handleModalClose} />
                    </Box>
                    {openModal.content === null ? (
                      <Plans values={values} onChangePlan={handleChange} isUpdateOnClick={true} onTablePlansModalOpen={handleModalOpen} />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'row'}}>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow sx={{ maxHeight: '150px' }}>
                                <TableCell sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center', justifyContent: 'center', borderRight: '1px solid #BDC9EE', borderBottom: 'none' }}>
                                  <Box
                                    sx={{
                                      height: '100%',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      textAlign: 'center',
                                      minHeight: '120px',
                                    }}
                                  >
                                    <Typography variant='h5Bold'>Find the right plan for you</Typography>
                                    <Typography variant='h10' sx={{ color: '#5E6A81' }}>Upgrade to experience the full power of TravGenius</Typography>
                                  </Box>
                                </TableCell>
                                {openModal.content.map((plan: IPlan, idx: number) => (
                                  <TableCell key={plan.id} align="center" sx={{ borderRight: `1px solid ${idx == 1 ? '#30CC7E' : idx === (plans.length - 1) ? 'transparent' : '#00ADE9'}`, borderBottom: 'none' }}>
                                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                      <Typography variant='h4Bold' className={styles.title}>
                                        {plan.type}
                                        {plan.type === "Elite" && <img src={OctaIcon} alt='checked' />}
                                      </Typography>
                                    </Box>
                                    <Typography variant='h6Bold' className={styles.payment}>
                                      {plan.price_in_cents === 0 ? 'Free' : `$${plan.price_in_cents / 100}`}
                                      {plan.price_in_cents !== 0 && <Typography variant='h13'>/year</Typography>}
                                    </Typography>
                                    <Typography variant='h10Bold' className={plan.id === planId ? styles.selectedTerms : styles.terms}>{plan.type === 'Basic' ? 'Get started' : 'Start 3-day trial'}</Typography>
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                            {allAdvantages.map((content, index) => (
                              <TableRow key={index}>
                                <TableCell sx={{ borderRight: '1px solid #BDC9EE', borderBottom: 'none' }}>
                                  <Typography variant='h12' sx={{ color: '#5E6A81' }}>
                                    {content}
                                  </Typography>
                                </TableCell>
                                {plans?.map((plan: IPlan, idx: number) => {
                                  const measure = getMeasure(plan, content);
                                  return (
                                    <TableCell key={plan.id} align="center" sx={{ borderRight: `1px solid ${idx == 1 ? '#30CC7E' : idx === (plans.length - 1) ? 'transparent' : '#00ADE9'}`, borderBottom: 'none' }}>
                                      <Typography variant='h12' sx={{ color: '#5E6A81' }}>
                                        {renderCellContent(measure)}
                                      </Typography>
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </Box>
                </Modal>
                <Modal
                  open={acknowledge}
                  onClose={(event, reason) => {
                    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                      setAcknowledge(false);
                    }
                  }}
                  aria-labelledby="acknowledge"
                  aria-describedby="acknowledge"
                  
                >
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    maxWidth: 870,
                    width: '100%',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: "20px",
                    padding: '30px 80px',
                    overflowY: 'auto',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '20px',
                  }}>
                    <img src={RoboHandImg} alt='checked' />
                    <Typography variant='h5Bold' className={styles.acknowledgeTitle}>AI-Powered Features Disclaimer</Typography>
                    <Typography variant='h10' sx={{ color: '#222B3A'}}>
                      TravGenius enhances your travel experience with smart tools and AI-powered features, but some functionalities are still evolving and may not always be perfect. Results may be inaccurate or unavailable at times. Use TravGenius as a guide, but always verify important details and keep copies of essential documents. 
                    </Typography>
                    <CustomButton
                      onClick={() => handleSubmitNext(values)}
                    >
                      Acknowledge
                    </CustomButton>
                  </Box>
                </Modal>
                {/* step 1 */}
                <Box sx={{ display: `${activeStep === 0 ? 'flex' : 'none'}`, marginTop: '50px' }} className={styles.stepWrapper}>
                  <Typography variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`} sx={{ textAlign: 'center' }}>Personalized travel starts here!</Typography>
                  <Typography variant='h10' className={styles.stepSubtitle}>Tell us a bit about yourself for a tailored journey</Typography>
                  <Box className={styles.stepFields}>
                    <CustomInput
                      inputType='text'
                      inputName='username'
                      required
                      inputLabel="Username"
                      value={values.username}
                      error={errors.username}
                      customClass={styles.customInput}
                      onChange={handleChange}
                    />
                    <CustomSelect
                      name="gender"
                      inputLabel='Gender'
                      handleChange={handleChange}
                      required={true}
                      value={values.gender}
                      data={['Male', 'Female', 'Non-binary', 'Prefer not to say']}
                    />
                    <CustomSelect
                      name="agegroup"
                      inputLabel='Age group'
                      handleChange={handleChange}
                      required={true}
                      value={values.agegroup}
                      data={['18-24', '25-34', '35-44', '45-54', '55-64', '65+']}
                    />
                    <CustomAutoSelect
                      options={countries ?? []}
                      inputLabel='Country'
                      placeholder='Enter country name'
                      handleChange={(e, value) => setFieldValue("country", value ? value.name : "")}
                      onInputChange={(event, inputValue) => {
                        setCountryInput(inputValue);
                      }}
                      value={values.country ? { name: values.country } : null}
                    />
                  </Box>

                  <Box className={styles.stepNav}>
                    <Box className={styles.stepsButtons}>
                      <CustomButton variant='outlined' disabled={isFirstStep} onClick={handleBack}>Back</CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant='contained'
                          disabled={(!values.username || !values.gender || !values.agegroup || !values.country)}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          Next
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 2 */}
                <Box sx={{ display: `${activeStep === 1 ? 'block' : 'none'}`, marginTop: '50px' }}>
                  <Plans values={values} onTablePlansModalOpen={handleModalOpen} onChangePlan={handleChange} />

                  <Box className={styles.stepNavPlan}>
                    <Box className={styles.planStepsButtons}>
                      <CustomButton variant='outlined' disabled={isFirstStep} onClick={handleBack}>Back</CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant='contained'
                          disabled={(!values.username || !values.gender || !values.agegroup || !values.country)}
                          onClick={() => setAcknowledge(true)}
                        >
                          Next
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 3 */}
                <Box sx={{ display: `${activeStep === 2 ? 'flex' : 'none'}`, marginTop: '50px' }} className={styles.stepWrapper}>
                  <Typography variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`} className={styles.stepTitle}>Let's personalize your AI assistant</Typography>
                  <Typography variant='h10' className={styles.stepSubtitle}>Where do you live and fly from?</Typography>
                  <Box className={styles.stepFields}>
                    <CustomAutoSelect
                      options={cities ?? []}
                      inputLabel='Nearest City'
                      placeholder='Enter city'
                      handleChange={(e, value) => setFieldValue("nearestCity", value ? value.name : "")}
                      onInputChange={(event, inputValue) => {
                        setCityInput(inputValue);
                      }}
                      value={values.nearestCity ? { name: values.nearestCity } : null}
                    />
                    <CustomAutoSelect
                      options={airports ?? []}
                      inputLabel='Nearest Airport'
                      placeholder='Enter airport'
                      handleChange={(e, value) => setFieldValue("nearestAirport", value ? value.name : "")}
                      onInputChange={(event, inputValue) => {
                        setAirportInput(inputValue);
                      }}
                      value={values.nearestAirport ? { name: values.nearestAirport } : null}
                    />
                    <CustomSelect
                      name="unitsofMeasure"
                      inputLabel='Units of Measure'
                      handleChange={handleChange}
                      value={values.unitsofMeasure}
                      data={['Miles/°F', 'Km/°C']}
                    />
                  </Box>
                  <Box className={styles.additionalInfo}>
                    <Typography variant='h11' className={styles.additionalInfoText}>To get badges, fill in all the questions</Typography>
                    <img src={InformationIcon} className={styles.additionalInfoImg} alt='info' />
                  </Box>

                  <Box className={styles.stepNav}>
                      <Typography variant='h10Semibold' onClick={() => handleSubmit(values)} className={styles.stepBtnSubtitle}>Personalize later</Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton variant='outlined' disabled={isFirstStep} onClick={handleBack}>Back</CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant='contained'
                          disabled={(!values.username || !values.gender || !values.agegroup || !values.country)}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          {!values.nearestCity && !values.unitsofMeasure ? 'Skip' : 'Next'}
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 4 */}
                <Box sx={{ display: `${activeStep === 3 ? 'flex' : 'none'}`, marginTop: '50px' }} className={styles.stepWrapper}>
                  <FormControl component="fieldset" className={styles.radioGroupWrapper}>
                    <Typography variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`} className={styles.stepTitle}>Let's personalize your AI assistant</Typography>
                    <Typography variant='h10' className={styles.stepSubtitle}>How do you travel?</Typography>
                    <RadioGroup name="travel" className={styles.radioGroup} value={values.travel} onChange={handleChange}>
                      {[
                        { name: "Solo travel", icon: SoloIcon },
                        { name: "Couples travel", icon: CouplesIcon },
                        { name: "Family travel", icon: FamilyIcon },
                        { name: "Friends travel", icon: FriendsIcon },
                        { name: "Work travel", icon: WorkTravelIcon }
                      ].map((val) => (
                        <FormControlLabel
                          key={val.name}
                          value={val.name}
                          control={<Radio sx={{ display: "none" }} />}
                          label={<RadioOption value={val.name} icon={val.icon} label={val.name.replace("_", " ")}
                            selected={values.travel === val.name}
                          />}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>

                  <FormControl component="fieldset" className={`${styles.radioGroupWrapper} ${styles.mt35}`}>
                    <FormLabel component="legend">Do you plan to invite others to collaborate?</FormLabel>
                    <RadioGroup name="collaborate" className={styles.radioGroup} value={values.collaborate} onChange={handleChange}>
                      {["yes", "no"].map((val) => (
                        <FormControlLabel
                          key={val}
                          value={val}
                          control={<Radio sx={{ display: "none" }} />}
                          label={<ToggleRadioButton value={val} label={val} selected={values.collaborate === val} />}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>

                  <Box className={styles.additionalInfo}>
                    <Typography variant='h11' className={styles.additionalInfoText}>To get badges, fill in all the questions</Typography>
                    <img src={InformationIcon} className={styles.additionalInfoImg} alt='info' />
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography variant='h10Semibold' onClick={() => handleSubmit(values)} className={styles.stepBtnSubtitle}>Personalize later</Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton variant='outlined' disabled={isFirstStep} onClick={handleBack}>Back</CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant='contained'
                          disabled={(!values.username || !values.gender || !values.agegroup || !values.country)}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          {!values.travel && !values.collaborate ? 'Skip' : 'Next'}
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 5 */}
                <Box sx={{ display: `${activeStep === 4 ? 'flex' : 'none'}`, marginTop: '50px' }} className={styles.stepWrapper}>
                  <FormControl component="fieldset" className={styles.radioGroupWrapper}>
                    <Typography variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`} className={styles.stepTitle}>Let's personalize your AI assistant</Typography>
                    <Typography variant='h10' className={styles.stepSubtitle}>Budget Preferences</Typography>
                    <RadioGroup name="budgetPreferences" className={styles.radioGroup} value={values.budgetPreferences} onChange={handleChange}>
                      {[
                        { value: "Economy", name: "Economy", icon: HomeIcon },
                        { value: "Mid-range", name: "Mid-Range", icon: CardIcon },
                        { value: "Luxury", name: "Luxury", icon: DiamondIcon },
                        { value: "Doesn't matter", name: "Doesn't Matter", icon: CheckMarkIcon },
                      ].map((val) => (
                        <FormControlLabel
                          key={val.value}
                          value={val.value}
                          control={<Radio sx={{ display: "none" }} />}
                          label={<RadioOption value={val.value} icon={val.icon} label={val.name.replace("_", " ")}
                            selected={values.budgetPreferences === val.value}
                          />}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>

                  <Box className={styles.additionalInfo}>
                    <Typography variant='h11' className={styles.additionalInfoText}>To get badges, fill in all the questions</Typography>
                    <img src={InformationIcon} className={styles.additionalInfoImg} alt='info' />
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography variant='h10Semibold' onClick={() => handleSubmit(values)} className={styles.stepBtnSubtitle}>Personalize later</Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton variant='outlined' disabled={isFirstStep} onClick={handleBack}>Back</CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant='contained'
                          disabled={(!values.username || !values.gender || !values.agegroup || !values.country)}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          {!values.budgetPreferences ? 'Skip' : 'Next'}
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 6 */}
                <Box sx={{ display: `${activeStep === 5 ? 'flex' : 'none'}`, marginTop: '50px' }} className={styles.stepWrapper}>
                  <FormControl component="fieldset" className={styles.radioGroupWrapper}>
                    <Typography variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`} className={styles.stepTitle}>Let's personalize your AI assistant</Typography>
                    <Typography variant='h10' className={styles.stepSubtitle}>Preferred Pace</Typography>
                    <RadioGroup name="pace" className={styles.radioGroup} value={values.pace} onChange={handleChange}>
                    {[
                      { value: "Fast-paced", name: "Fast-Paced", icon: ThunderIcon },
                      { value: "Balanced", name: "Balanced", icon: BalanceIcon },
                      { value: "Slow & Relaxed", name: "Slow & Relaxed", icon: SofaIcon }
                    ].map((val) => (
                      <FormControlLabel
                        key={val.value}
                        value={val.value}
                        control={<Radio sx={{ display: "none" }} />}
                        label={<RadioOption value={val.value} icon={val.icon} label={val.name.replace("_", " ")}
                          selected={values.pace === val.value}
                        />}
                      />
                    ))}
                    </RadioGroup>
                  </FormControl>

                  <FormControl component="fieldset" className={`${styles.radioGroupWrapper} ${styles.mt35}`}>
                    <FormLabel component="legend">Trip Planning Style</FormLabel>
                    <RadioGroup name="planningStyle" className={styles.radioGroup} value={values.planningStyle} onChange={handleChange}>
                      {["Structured", "Flexible", "Spontaneous"].map((val) => (
                        <FormControlLabel
                          key={val}
                          value={val}
                          control={<Radio sx={{ display: "none" }} />}
                          label={<ToggleRadioButton value={val} label={val} selected={values.planningStyle === val} />}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>

                  <Box className={styles.additionalInfo}>
                    <Typography variant='h11' className={styles.additionalInfoText}>To get badges, fill in all the questions</Typography>
                    <img src={InformationIcon} className={styles.additionalInfoImg} alt='info' />
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography variant='h10Semibold' onClick={() => handleSubmit(values)} className={styles.stepBtnSubtitle}>Personalize later</Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton variant='outlined' disabled={isFirstStep} onClick={handleBack}>Back</CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant='contained'
                          disabled={(!values.username || !values.gender || !values.agegroup || !values.country)}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          {!values.pace && !values.planningStyle ? 'Skip' : 'Next'}
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 7 */}
                <Box sx={{ display: `${activeStep === 6 ? 'flex' : 'none'}`, marginTop: '50px' }} className={styles.stepWrapper}>
                  <FormControl component="fieldset" className={`${styles.radioGroupWrapper} ${styles.mt35}`}>
                    <Typography variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`} className={styles.stepTitle}>Let's personalize your AI assistant</Typography>
                    <Typography variant='h10' className={styles.stepSubtitle}>Primary Travel Purpose (Select Upto {planType.includes('Basic') || planType === '' ? 3 : 6})</Typography>
                    <FormGroup className={styles.checkboxesGroup}>
                      {primaryTravelPurpose?.map((option: {name: string, is_free: boolean}) => {
                        const isChecked = values.travelPurpose.includes(option.name);
                        const reachMaxLimit = values.travelPurpose.length >= (planType.includes('Basic') ? 3 : 6) && !isChecked;
                        const isDisabled = (planType.includes('Basic') || planType === '') && !option.is_free;
                        const finalDisabled = isDisabled || reachMaxLimit;

                        return (
                          <FormControlLabel
                            key={option.name}
                            className={`${styles.checkLabel} ${isChecked ? styles.checked : ""} ${finalDisabled ? styles.disabledOption : ''}`}
                            control={
                              <Checkbox
                                className={styles.checkbox}
                                checked={isChecked}
                                disabled={finalDisabled}
                                onChange={(event) => {
                                  if (event.target.checked) {
                                    setFieldValue("travelPurpose", !!values.travelPurpose[0]?.length ?  [...values.travelPurpose, option.name] : [option.name]);
                                  } else {
                                    setFieldValue("travelPurpose", values.travelPurpose.filter((item: string) => item !== option.name));
                                  }
                                }}
                              />
                            }
                            label={
                              <Typography variant={isChecked ? 'h10Semibold' : 'h10'} className={finalDisabled ? styles.disabledOptionText : ''}>
                                {option.name}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  </FormControl>

                  <Box className={styles.additionalInfo}>
                    <Typography variant='h11' className={styles.additionalInfoText}>To get badges, fill in all the questions</Typography>
                    <img src={InformationIcon} className={styles.additionalInfoImg} alt='info' />
                  </Box>

                  <Box className={`${styles.fullAccessLabel} ${planType.includes('Basic') || !planType.length ? '' : styles.fullAccessLabelHide}`} onClick={() => handleModalOpen(null)}>
                    <Box className={styles.fullAccessTextContent}>
                      <Typography variant='h8Bold'>Would you like more options?</Typography>
                      <Typography variant='h11'>To get full access, upgrade your plan</Typography>
                    </Box>
                    <Typography variant='h10Bold' className={styles.fullAccessBtn}>Get full access</Typography>
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography variant='h10Semibold' onClick={() => handleSubmit(values)} className={styles.stepBtnSubtitle}>Personalize later</Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton variant='outlined' disabled={isFirstStep} onClick={handleBack}>Back</CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant='contained'
                          disabled={(!values.username || !values.gender || !values.agegroup || !values.country)}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          {values.travelPurpose.length === 0 || values.travelPurpose[0].length === 0 ? 'Skip' : 'Next'}
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 8 */}
                <Box sx={{ display: `${activeStep === 7 ? 'flex' : 'none'}`, marginTop: '50px' }} className={styles.stepWrapper}>
                  <FormControl component="fieldset" className={`${styles.radioGroupWrapper} ${styles.mt35}`}>
                    <Typography variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`} className={styles.stepTitle}>Let's personalize your AI assistant</Typography>
                    <Typography variant='h10' className={styles.stepSubtitle}>Types Of Destinations (Select Upto {planType.includes('Basic') || planType === '' ? 3 : 6})</Typography>
                    <FormGroup className={styles.checkboxesGroup}>
                      {typeOfDestination?.map((option: {name: string, is_free: boolean}) => {
                        const isChecked = values.destinations.includes(option.name);
                        const reachMaxLimit = values.destinations.length >= (planType.includes('Basic') ? 3 : 6) && !isChecked;
                        const isDisabled = (planType.includes('Basic') || planType === '') && !option.is_free;
                        const finalDisabled = isDisabled || reachMaxLimit;

                        return (
                          <FormControlLabel
                            key={option.name}
                            className={`${styles.checkLabel} ${isChecked ? styles.checked : ""} ${finalDisabled ? styles.disabledOption : ''}`}
                            control={
                              <Checkbox
                                className={styles.checkbox}
                                checked={isChecked}
                                disabled={finalDisabled}
                                onChange={(event) => {
                                  if (event.target.checked) {
                                    setFieldValue("destinations", !!values.destinations[0]?.length ?  [...values.destinations, option.name] : [option.name]);
                                  } else {
                                    setFieldValue("destinations", values.destinations.filter((item: string) => item !== option.name));
                                  }
                                }}
                              />
                            }
                            label={
                              <Typography variant={isChecked ? 'h10Semibold' : 'h10'} className={finalDisabled ? styles.disabledOptionText : ''}>
                                {option.name}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  </FormControl>

                  <Box className={styles.additionalInfo}>
                    <Typography variant='h11' className={styles.additionalInfoText}>To get badges, fill in all the questions</Typography>
                    <img src={InformationIcon} className={styles.additionalInfoImg} alt='info' />
                  </Box>

                  <Box className={`${styles.fullAccessLabel} ${planType.includes('Basic') || !planType.length ? '' : styles.fullAccessLabelHide}`} onClick={() => handleModalOpen(null)}>
                    <Box className={styles.fullAccessTextContent}>
                      <Typography variant='h8Bold'>Would you like more options?</Typography>
                      <Typography variant='h11'>To get full access, upgrade your plan</Typography>
                    </Box>
                    <Typography variant='h10Bold' className={styles.fullAccessBtn}>Get full access</Typography>
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography variant='h10Semibold' onClick={() => handleSubmit(values)} className={styles.stepBtnSubtitle}>Personalize later</Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton variant='outlined' disabled={isFirstStep} onClick={handleBack}>Back</CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant='contained'
                          disabled={(!values.username || !values.gender || !values.agegroup || !values.country)}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          {values.destinations.length === 0 || values.destinations[0].length === 0 ? 'Skip' : 'Next'}
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step9 */}
                <Box sx={{ display: `${activeStep === 8 ? 'flex' : 'none'}`, marginTop: '50px' }} className={styles.stepWrapper}>
                  <FormControl component="fieldset" className={`${styles.radioGroupWrapper} ${styles.mt35}`}>
                    <Typography variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`} className={styles.stepTitle}>Let's personalize your AI assistant</Typography>
                    <Typography variant='h10' className={styles.stepSubtitle}>Activity Interests (Select Upto {planType.includes('Basic') || planType === '' ? 3 : 6})</Typography>
                    <FormGroup className={styles.checkboxesGroup}>
                      {activityInterest?.map((option: {name: string, is_free: boolean}) => {
                        const isChecked = values.interests.includes(option.name);
                        const reachMaxLimit = values.interests.length >= (planType.includes('Basic') ? 3 : 6) && !isChecked;
                        const isDisabled = (planType.includes('Basic') || planType === '') && !option.is_free;
                        const finalDisabled = isDisabled || reachMaxLimit;

                        return (
                          <FormControlLabel
                            key={option.name}
                            className={`${styles.checkLabel} ${isChecked ? styles.checked : ""} ${finalDisabled ? styles.disabledOption : ''}`}
                            control={
                              <Checkbox
                                className={styles.checkbox}
                                checked={isChecked}
                                disabled={finalDisabled}
                                onChange={(event) => {
                                  if (event.target.checked) {
                                    setFieldValue("interests", !!values.interests[0]?.length ?  [...values.interests, option.name] : [option.name]);
                                  } else {
                                    setFieldValue("interests", values.interests.filter((item: string) => item !== option.name));
                                  }
                                }}
                              />
                            }
                            label={
                              <Typography variant={isChecked ? 'h10Semibold' : 'h10'} className={finalDisabled ? styles.disabledOptionText : ''}>
                                {option.name}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  </FormControl>

                  <Box className={styles.additionalInfo}>
                    <Typography variant='h11' className={styles.additionalInfoText}>To get badges, fill in all the questions</Typography>
                    <img src={InformationIcon} className={styles.additionalInfoImg} alt='info' />
                  </Box>

                  <Box className={`${styles.fullAccessLabel} ${planType.includes('Basic') || !planType.length ? '' : styles.fullAccessLabelHide}`} onClick={() => handleModalOpen(null)}>
                    <Box className={styles.fullAccessTextContent}>
                      <Typography variant='h8Bold'>Would you like more options?</Typography>
                      <Typography variant='h11'>To get full access, upgrade your plan</Typography>
                    </Box>
                    <Typography variant='h10Bold' className={styles.fullAccessBtn}>Get full access</Typography>
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography variant='h10Semibold' onClick={() => handleSubmit(values)} className={styles.stepBtnSubtitle}>Personalize later</Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton variant='outlined' disabled={isFirstStep} onClick={handleBack}>Back</CustomButton>
                      {isLastStep && (
                        <CustomButton
                          btnType='submit'
                          // disabled={isSubmitting}
                        >
                          Finish
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Form>)}
          </Formik>
        </Box>
      </Box>
    </Container>
  )
}

export default PersonalizedForm;
